import { useAuth } from "@/_core/hooks/useAuth";
import { SitePageShell } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { getLoginUrl } from "@/const";
import { getOrCreateGenerationSessionId } from "@/lib/generationSession";
import { trpc } from "@/lib/trpc";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  ImageIcon,
  Loader2,
  MessageSquare,
  Palette,
  RefreshCw,
  Send,
  Sparkles,
  Upload,
  Wand2,
  X,
  Zap,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

/* ============================================================
 * Style Presets — 감성 기반 (화가 이름 비노출)
 * ============================================================ */
const STYLE_PRESETS = [
  {
    value: "seongsu",
    icon: "🏙",
    label: "성수동 감성",
    description: "현대적이고 감성적인 갤러리 분위기",
    recommendedFor: ["반려동물", "자동차", "카페"],
  },
  {
    value: "newyork",
    icon: "🗽",
    label: "뉴욕 갤러리",
    description: "대담하고 강렬한 현대 미술",
    recommendedFor: ["인물", "자동차", "건축"],
  },
  {
    value: "paris",
    icon: "🎨",
    label: "파리 아틀리에",
    description: "우아하고 클래식한 유럽 회화",
    recommendedFor: ["인물", "커플", "풍경"],
  },
  {
    value: "nordic",
    icon: "🌲",
    label: "북유럽",
    description: "따뜻하고 편안한 거실 분위기",
    recommendedFor: ["가족", "부모님", "아이"],
  },
  {
    value: "jazzbar",
    icon: "🎷",
    label: "재즈바",
    description: "무디하고 감각적인 밤 분위기",
    recommendedFor: ["인물", "커플", "반려동물"],
  },
  {
    value: "cosmic",
    icon: "🌌",
    label: "우주",
    description: "신비롭고 몽환적인 판타지",
    recommendedFor: ["반려동물", "인물", "자동차"],
  },
  {
    value: "minimal",
    icon: "🏡",
    label: "미니멀",
    description: "깔끔하고 모던한 인테리어 아트",
    recommendedFor: ["반려동물", "인물", "정물"],
  },
  {
    value: "luxury",
    icon: "✨",
    label: "럭셔리",
    description: "고급스럽고 화려한 프리미엄 아트",
    recommendedFor: ["인물", "자동차", "웨딩"],
  },
  {
    value: "movie_poster",
    icon: "🎬",
    label: "영화 포스터",
    description: "강렬하고 드라마틱한 연출",
    recommendedFor: ["자동차", "커플", "웨딩"],
  },
  {
    value: "ai_recommend",
    icon: "🤖",
    label: "AI 추천",
    description: "사진을 분석하여 최적 스타일 추천",
    recommendedFor: ["모든 사진"],
  },
];

const QUICK_ACTIONS = [
  { id: "preserve_face", label: "얼굴 보존", desc: "인물 얼굴 정확히 유지" },
  { id: "keep_composition", label: "구도 유지", desc: "원본 배치 그대로" },
  { id: "enhance_color", label: "색감 강화", desc: "더 풍부한 색상" },
  { id: "soft_brushstroke", label: "부드러운 붓질", desc: "섬세한 터치" },
  { id: "heavy_texture", label: "두꺼운 질감", desc: "임파스토 효과" },
  { id: "background_blur", label: "배경 흐림", desc: "피사체 강조" },
];

const SLIDER_DEFAULTS = {
  oil_intensity: 70,
  brush_detail: 60,
  color_saturation: 65,
  canvas_texture: 50,
  light_contrast: 55,
  warmth: 60,
  sharpness: 50,
  artistic_freedom: 40,
  frame_effect: 30,
};

const SLIDER_LABELS: Record<string, string> = {
  oil_intensity: "유화 강도",
  brush_detail: "붓질 디테일",
  color_saturation: "색감 채도",
  canvas_texture: "캔버스 질감",
  light_contrast: "명암 대비",
  warmth: "따뜻함",
  sharpness: "선명도",
  artistic_freedom: "변형 자유도",
  frame_effect: "프레임 효과",
};

/* 생성 중 랜덤 메시지 */
const LOADING_MESSAGES = [
  "사진을 천천히 바라보고 있어요.",
  "분위기를 찾고 있어요.",
  "색감을 다듬고 있어요.",
  "붓터치를 정리하고 있어요.",
  "마지막 디테일을 더하고 있어요.",
  "구도를 잡고 있어요.",
  "빛의 방향을 정하고 있어요.",
  "작품의 이야기를 담고 있어요.",
];

/* ============================================================
 * PRECISE_EDIT confirmation summary (변경/유지/방식)
 * ============================================================
 * PRECISE_EDIT responses never include a server-computed understandingSummary
 * (only ART_TRANSFORM's full Deep Art Director pipeline builds one), so this
 * mirrors it on the client from fields the existing analyzeIntent response
 * already returns — no extra LLM call, no server-side change. The preserve
 * list intentionally mirrors promptDirector.ts's buildPrecisePreserveList
 * conditions (same intent flags) purely for display; it does not affect the
 * actual prompt sent to the image model.
 */
function buildPreciseEditSummary(intent: any): {
  change: string;
  preserve: string;
  method: string;
} {
  const rawSummary: string =
    (intent?.user_intent_summary as string | undefined)?.trim() ||
    (intent?.precise_change_instruction as string | undefined)?.trim() ||
    "요청하신 부분만 정밀하게 수정합니다.";
  // user_intent_summary is often a full sentence ("...변경합니다. 나머지는
  // 유지합니다."); the first sentence alone is enough for the "변경" line and
  // avoids duplicating the "유지" line below.
  const change = rawSummary.split(/(?<=[.!?])\s/)[0] || rawSummary;

  // Base items mirror promptDirector.ts's unconditional PRESERVE entries
  // (face/eyes/ears/pose); background and composition are the two flags
  // most PRECISE_EDIT requests actually vary. preserve_clothing/color/
  // people_count/hands are left out here — the schema defaults them to
  // true on nearly every request regardless of whether the subject even
  // has clothing/hands in frame, so including them made this line noisy
  // without conveying anything the customer couldn't already assume.
  const preserveItems = ["얼굴", "눈", "귀", "자세"];
  if (intent?.preserve_background !== false) preserveItems.push("배경");
  if (intent?.preserve_composition !== false) preserveItems.push("구도");

  return {
    change,
    preserve: `${preserveItems.join("/")} 유지`,
    method: "원본 보존형 정밀 수정",
  };
}

/* ============================================================
 * Main Component
 * ============================================================ */
export default function CreatePage() {
  const { user, isAuthenticated, loading } = useAuth();
  const [location, navigate] = useLocation();

  // Customer-facing entry-point framing only (heading/copy/default style) —
  // does NOT touch request_mode classification, Prompt Director, or model
  // selection. The classifier (server/services/artDirector.ts) still makes
  // its own PRECISE_EDIT/ART_TRANSFORM decision from the actual prompt text
  // and whether a style was picked, exactly as before.
  // Named entryMode (not "mode") — this file already has a `mode` state
  // further down ("generate" | "edit", sent to analyzeIntent/generateSmart)
  // that is unrelated and must not be shadowed.
  const entryMode = useMemo(() => {
    const m = new URL(window.location.href).searchParams.get("mode");
    return m === "oil" || m === "custom" ? m : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const pageHeading =
    entryMode === "oil" ? "유화 작품 만들기" : entryMode === "custom" ? "맞춤 작품 만들기" : "아르와 작품 만들기";
  const pageSubheading =
    entryMode === "oil"
      ? "사진을 업로드하면 아르가 유화 작품으로 만들어 드립니다. 원하는 분위기가 있다면 자유롭게 적어주세요."
      : entryMode === "custom"
        ? "원하시는 변경 내용을 자유롭게 적어주세요. 아르가 요청에 맞게 작품을 만들어 드립니다."
        : "사진을 업로드하고 스타일을 선택하면, 아르가 작품으로 만들어 드립니다.";
  const promptPlaceholder =
    entryMode === "custom"
      ? "예: 배경을 들판으로 바꿔주세요 / 털 색을 흰색으로 바꿔주세요 / 따뜻한 유화 분위기로 만들어주세요"
      : "예: 배경을 성수동 갤러리 느낌으로, 코랄 핑크 색감으로 만들어줘";

  // One artwork = one session: reload resumes it; Home's "새 작품 만들기"
  // entry replaces it (see lib/generationSession.ts).
  const [sessionId] = useState(() => getOrCreateGenerationSessionId());

  // Core state
  const [prompt, setPrompt] = useState("");
  // null = 스타일 미선택("AI가 요청에 맞게 판단"). Only set once the user
  // actually clicks a style card — see the analyzeIntent call below, which
  // must NOT send a stylePreset in this case (server treats an explicit
  // stylePreset as "always ART_TRANSFORM"; omitting it lets simple
  // PRECISE_EDIT requests like "고양이 털만 흰색으로" classify correctly).
  const [style, setStyle] = useState<string | null>(() => (entryMode === "oil" ? "seongsu" : null));
  const [emotion, setEmotion] = useState<string | null>(null);
  const [quickActions, setQuickActions] = useState<string[]>(["preserve_face", "keep_composition"]);
  const [sliders, setSliders] = useState(SLIDER_DEFAULTS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mode, setMode] = useState<"generate" | "edit">("generate");

  // Reference image
  const [refPreview, setRefPreview] = useState<string | null>(null);
  const [refBase64, setRefBase64] = useState<string | null>(null);
  const [refMime, setRefMime] = useState("image/png");
  const [refImageId, setRefImageId] = useState<number | undefined>();
  const [refFileKey, setRefFileKey] = useState<string | undefined>();

  // Results
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [artMemo, setArtMemo] = useState<string | null>(null);
  const isGeneratingRef = useRef(false);

  // Understanding Summary (시스템 이해 내용 한국어 표시)
  const [understandingSummary, setUnderstandingSummary] = useState<{
    preservationSummary: string;
    backgroundSummary: string;
    styleSummary: string;
  } | null>(null);
  // PRECISE_EDIT confirmation summary (변경/유지/방식) — built entirely on
  // the client from the existing analyzeIntent response; the server's
  // PRECISE_EDIT short-circuit never computes an understandingSummary, so
  // without this the confirmation card had nothing to show but its title
  // and the confirm/cancel buttons.
  const [preciseEditSummary, setPreciseEditSummary] = useState<{
    change: string;
    preserve: string;
    method: string;
  } | null>(null);
  const [pendingGeneration, setPendingGeneration] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Mutually-exclusive screen phase for the results panel. Layout below keys
  // off this (set synchronously, in the same batch as the state it pairs
  // with) instead of react-query's isPending flags, which flip on their own
  // schedule and used to produce a transient render (Empty State) between
  // "analyzing" and "confirmation".
  const [phase, setPhase] = useState<"idle" | "analyzing" | "confirmation" | "generating" | "result">("idle");
  const isBusy = phase === "analyzing" || phase === "generating";

  // Image Preview Modal
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [previewImageLoading, setPreviewImageLoading] = useState(false);

  // AI Chat
  const [chatMessages, setChatMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState<string | null>(null);

  // Loading message rotation
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const loadingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const utils = trpc.useUtils();
  const sessionImages = trpc.images.listSession.useQuery(
    { sessionId },
    { enabled: isAuthenticated },
  );

  const uploadRef = trpc.images.uploadReference.useMutation({
    onSuccess: (data) => {
      setRefImageId(data.id);
      setRefFileKey(data.fileKey);
      toast.success("사진 업로드 완료");
    },
    onError: (e) => toast.error(`업로드 실패: ${e.message}`),
  });

  const analyzeIntent = trpc.artDirector.analyze.useMutation();
  const generateSmart = trpc.artDirector.generateSmart.useMutation({
    onSuccess: (data) => {
      utils.images.listSession.invalidate({ sessionId });
      isGeneratingRef.current = false;
      if (data.artMemo) {
        setArtMemo(data.artMemo);
      }
      if (data.partialErrors?.length) {
        const msg = data.partialErrors[data.partialErrors.length - 1];
        setGenerationError(msg);
        toast.warning(msg);
      } else {
        setGenerationError(null);
        toast.success("작품이 완성되었습니다!");
      }
    },
    onError: (e) => {
      isGeneratingRef.current = false;
      setGenerationError(e.message);
      toast.error("이미지 생성 실패");
    },
  });

  // Legacy generate fallback
  const generateLegacy = trpc.images.generate.useMutation({
    onSuccess: () => {
      utils.images.listSession.invalidate({ sessionId });
      setGenerationError(null);
      isGeneratingRef.current = false;
      toast.success("작품이 완성되었습니다!");
    },
    onError: (e) => {
      isGeneratingRef.current = false;
      setGenerationError(e.message);
      toast.error("이미지 생성 실패");
    },
  });

  const analyzePhoto = trpc.artDirector.analyzePhoto.useMutation({
    onSuccess: (data) => {
      if (data.recommended_style) {
        setStyle(data.recommended_style);
        const styleLabel = STYLE_PRESETS.find(s => s.value === data.recommended_style)?.label ?? data.recommended_style;
        const typeLabels: Record<string, string> = {
          portrait: "인물 사진", pet: "반려동물", landscape: "풍경",
          wedding: "웨딩", family: "가족", object: "사물", other: "일반",
        };
        setAiRecommendation(`${typeLabels[data.photo_type] ?? "사진"} 감지 → ${styleLabel} 추천`);
        const actions: string[] = ["keep_composition"];
        if (data.has_face || data.recommended_settings?.preserve_face) actions.push("preserve_face");
        setQuickActions(actions);
        toast.success("AI가 사진을 분석하고 최적 스타일을 추천했습니다!");
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      // Preserve ?mode=oil/custom (not just the bare path) so a visitor who
      // lands directly on /create?mode=... while logged out — rather than
      // via Home.tsx's goTo(), which already handles this — still gets the
      // right entry-mode framing back after login.
      window.location.href = getLoginUrl(window.location.pathname + window.location.search);
    }
  }, [loading, isAuthenticated]);

  // Loading message rotation effect
  const isLoading = generateSmart.isPending || generateLegacy.isPending || uploadRef.isPending || analyzeIntent.isPending;

  useEffect(() => {
    if (isLoading) {
      let idx = 0;
      setLoadingMsg(LOADING_MESSAGES[0]);
      loadingIntervalRef.current = setInterval(() => {
        idx = (idx + 1) % LOADING_MESSAGES.length;
        setLoadingMsg(LOADING_MESSAGES[idx]);
      }, 3000);
    } else {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    }
    return () => {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
    };
  }, [isLoading]);

  /* ----- Handlers ----- */
  const handleFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("10MB 이하 파일만 업로드 가능합니다.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("이미지 파일만 업로드 가능합니다.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setRefPreview(dataUrl);
      const base64 = dataUrl.split(",")[1] ?? "";
      setRefBase64(base64);
      setRefMime(file.type || "image/png");
      setRefImageId(undefined);
      setRefFileKey(undefined);
    };
    reader.readAsDataURL(file);
  };

  const uploadIfNeeded = async () => {
    if (refImageId && refFileKey) return { id: refImageId, fileKey: refFileKey };
    if (!refBase64) return {};
    const res = await uploadRef.mutateAsync({ base64: refBase64, mimeType: refMime });
    setRefFileKey(res.fileKey);
    return { id: res.id, fileKey: res.fileKey };
  };

  const runGenerate = async () => {
    if (isGeneratingRef.current || generateSmart.isPending || generateLegacy.isPending) return;
    if (!prompt.trim() && !refBase64) {
      toast.error("사진을 업로드하거나 작품 설명을 입력해주세요.");
      return;
    }

    isGeneratingRef.current = true;
    setGenerationError(null);
    setPhase("analyzing");

    try {
      const ref = await uploadIfNeeded();

      const intentResult = await analyzeIntent.mutateAsync({
        sessionId,
        message: prompt.trim() || "이 사진을 작품으로 변환해주세요",
        conversationHistory: chatMessages,
        hasReferenceImage: !!ref.fileKey || !!refBase64,
        quickActions,
        sliderOverrides: sliders,
        // Omit entirely when no style was actually picked — sending "" (or
        // any string) would still count as "explicitly selected" server-side.
        ...(style ? { stylePreset: style } : {}),
        emotionPreset: emotion ?? undefined,
        mode,
      });

      if (intentResult.needsClarification && intentResult.clarificationQuestion) {
        const question = intentResult.clarificationQuestion;
        setChatMessages((prev) => [
          ...prev,
          { role: "user" as const, content: prompt.trim() || "작품으로 변환해주세요" },
          { role: "assistant" as const, content: question },
        ]);
        setShowChat(true);
        isGeneratingRef.current = false;
        setPhase("idle");
        toast.info("아르가 추가 질문이 있습니다. 답변 후 다시 생성해주세요.");
        return;
      }

      const hasReference = !!(ref.fileKey || refBase64);

      // v8: 원본 이미지 없으면 생성 차단
      if (!hasReference) {
        toast.error("원본 사진이 필요합니다. 사진을 업로드해주세요.");
        isGeneratingRef.current = false;
        setPhase("idle");
        return;
      }

      // v8: 시스템 이해 내용 표시
      // ART_TRANSFORM returns a server-computed understandingSummary; PRECISE_EDIT
      // does not (its short-circuit path skips that pipeline entirely), so its
      // confirmation summary (변경/유지/방식) is built here from the already-
      // fetched intent — no extra LLM call, no server change.
      if (intentResult.understandingSummary) {
        setUnderstandingSummary(intentResult.understandingSummary);
        setPreciseEditSummary(null);
      } else if (intentResult.requestMode === "PRECISE_EDIT") {
        setUnderstandingSummary(null);
        setPreciseEditSummary(buildPreciseEditSummary(intentResult.intent));
      } else {
        setUnderstandingSummary(null);
        setPreciseEditSummary(null);
      }

      if (intentResult.builtPrompt && hasReference) {
        const genPayload = {
          sessionId,
          intent: intentResult.intent,
          builtPrompt: intentResult.builtPrompt,
          referenceFileKey: ref.fileKey,
          referenceBase64: !ref.fileKey && refBase64 ? refBase64 : undefined,
          referenceMimeType: refMime,
          referenceImageId: ref.id,
          mode: "edit" as const,
          count: 1,
          autoRetry: true,
        };
        // v8: 확인 단계 — 이해 요약을 보여주고 사용자 확인 후 생성 진행
        // Same synchronous batch as the summary state above: analyzing →
        // confirmation in one commit, no in-between render.
        setPendingGeneration(genPayload);
        setShowConfirmation(true);
        setPhase("confirmation");
        isGeneratingRef.current = false;
        return;
      } else if (!intentResult.builtPrompt) {
        toast.error("프롬프트 생성에 실패했습니다. 다시 시도해주세요.");
        isGeneratingRef.current = false;
        setPhase("idle");
        return;
      }
      setPhase("idle");
    } catch {
      isGeneratingRef.current = false;
      setPhase("idle");
    }
  };

  /** 확인 후 실제 생성 실행 */
  const confirmAndGenerate = async () => {
    if (!pendingGeneration) return;
    setShowConfirmation(false);
    setPhase("generating");
    isGeneratingRef.current = true;
    let succeeded = false;
    try {
      await generateSmart.mutateAsync(pendingGeneration);
      // Wait for the refreshed image list so "result" never renders an
      // empty grid (→ Empty State flash) before the new image arrives.
      await utils.images.listSession.invalidate({ sessionId });
      succeeded = true;
    } catch {
      // error handled by mutation callbacks
    } finally {
      isGeneratingRef.current = false;
      setPendingGeneration(null);
      setPhase(succeeded ? "result" : "idle");
    }
  };

  /** 수정하기 — 확인 단계 취소 */
  const cancelConfirmation = () => {
    setShowConfirmation(false);
    setPhase("idle");
    setPendingGeneration(null);
    setUnderstandingSummary(null);
    setPreciseEditSummary(null);
    toast.info("요청을 수정한 후 다시 시도해주세요.");
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: msg }]);
    setPrompt(msg);
  };

  const toggleQuickAction = (id: string) => {
    setQuickActions((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const goNext = () => {
    if (!selectedId) {
      toast.error("작품을 하나 선택해주세요.");
      return;
    }
    // entryMode is forwarded (not yet consumed by Options.tsx) so a future
    // step can price 유화/맞춤 differently without re-threading this value
    // through Create.tsx again — see the pricing gap noted in this round's report.
    navigate(`/options?image=${selectedId}${entryMode ? `&mode=${entryMode}` : ""}`);
  };

  const orderedImages = useMemo(
    () => (sessionImages.data ?? []).filter((img) => img.url && img.status !== "failed"),
    [sessionImages.data],
  );

  // Large-preview keyboard support (works without the dialog having focus)
  // and background scroll lock while it's open. Selection logic untouched.
  useEffect(() => {
    if (previewIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewIndex(null);
      else if (e.key === "ArrowLeft") setPreviewIndex((i) => (i !== null && i > 0 ? i - 1 : i));
      else if (e.key === "ArrowRight")
        setPreviewIndex((i) => (i !== null && i < orderedImages.length - 1 ? i + 1 : i));
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [previewIndex === null, orderedImages.length]);

  if (loading || !isAuthenticated) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  return (
    <SitePageShell>
      <div className="container py-4 md:py-8 max-w-7xl mx-auto">
        <StepIndicator current={1} />

        <div className="mt-6 grid lg:grid-cols-12 gap-5 lg:gap-8">
          {/* Left Panel - Controls */}
          <div className="lg:col-span-5 space-y-4">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-primary">
                {pageHeading}
              </h1>
              <p className="text-muted-foreground mt-1 text-sm">
                {pageSubheading}
              </p>
            </div>

            <Card className="border-border/60">
              <CardContent className="p-4 space-y-4">
                {/* Photo Upload */}
                <div>
                  <Label className="mb-1.5 block text-sm font-medium">사진 업로드</Label>
                  {refPreview ? (
                    <div className="relative inline-block w-full">
                      <img
                        src={refPreview}
                        alt="참고 이미지"
                        className="rounded-lg w-full max-h-44 object-cover border border-border/60"
                      />
                      <button
                        onClick={() => {
                          setRefPreview(null);
                          setRefBase64(null);
                          setRefImageId(undefined);
                          setRefFileKey(undefined);
                          setAiRecommendation(null);
                        }}
                        className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      {aiRecommendation && (
                        <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-2.5 py-1.5 rounded-md">
                          <Zap className="w-3 h-3 inline mr-1 text-yellow-400" />
                          {aiRecommendation}
                        </div>
                      )}
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-border/80 rounded-lg p-6 text-muted-foreground hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer">
                      <Upload className="w-7 h-7 mb-2 opacity-60" />
                      <span className="text-sm font-medium">사진을 선택하세요</span>
                      <span className="text-xs mt-1 opacity-70">JPG, PNG (최대 10MB)</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleFile(f);
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* Style Presets — Card Grid */}
                <div>
                  <Label className="mb-2 block text-sm font-medium">
                    <Palette className="w-3.5 h-3.5 inline mr-1" />
                    스타일 선택
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {STYLE_PRESETS.map((s) => (
                      <button
                        key={s.value}
                        onClick={() => setStyle(s.value)}
                        className={`text-left rounded-xl border p-2.5 transition-all ${
                          style === s.value
                            ? "border-primary bg-primary/5 ring-1 ring-primary/30 shadow-sm"
                            : "border-border/60 hover:border-primary/40 hover:bg-muted/30"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-lg leading-none mt-0.5">{s.icon}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold truncate">{s.label}</div>
                            <div className="text-[10px] text-muted-foreground leading-tight mt-0.5 line-clamp-1">
                              {s.description}
                            </div>
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {s.recommendedFor.slice(0, 3).map((r) => (
                                <span
                                  key={r}
                                  className="text-[9px] bg-muted/80 text-muted-foreground px-1.5 py-0.5 rounded"
                                >
                                  {r}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <Label className="mb-1.5 block text-sm font-medium">
                    <Zap className="w-3.5 h-3.5 inline mr-1" />
                    빠른 설정
                  </Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {QUICK_ACTIONS.map((qa) => (
                      <button
                        key={qa.id}
                        onClick={() => toggleQuickAction(qa.id)}
                        className={`text-left rounded-lg border px-2.5 py-2 transition-all ${
                          quickActions.includes(qa.id)
                            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                            : "border-border/60 hover:border-primary/40"
                        }`}
                      >
                        <div className="text-xs font-medium">{qa.label}</div>
                        <div className="text-[10px] text-muted-foreground">{qa.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Advanced Sliders */}
                <div>
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  >
                    <span>{showAdvanced ? "▼" : "▶"}</span>
                    세부 조절 (슬라이더 {showAdvanced ? "닫기" : "열기"})
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3">
                      {Object.entries(SLIDER_LABELS).map(([key, label]) => (
                        <div key={key} className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{label}</span>
                            <span className="font-medium text-primary">{sliders[key as keyof typeof sliders]}</span>
                          </div>
                          <Slider
                            value={[sliders[key as keyof typeof sliders]]}
                            onValueChange={([v]) => setSliders((p) => ({ ...p, [key]: v }))}
                            max={100}
                            step={5}
                            className="w-full"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Prompt */}
                <div>
                  <Label className="mb-1.5 block text-sm font-medium">
                    작품 설명 <span className="text-muted-foreground font-normal">(선택)</span>
                  </Label>
                  <Textarea
                    rows={2}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={promptPlaceholder}
                    className="resize-none text-sm"
                  />
                </div>

                {/* Generate Button */}
                <Button
                  onClick={runGenerate}
                  disabled={isLoading}
                  size="lg"
                  className="w-full text-base"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      아르가 작업 중...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {orderedImages.length > 0 ? "아르와 함께 다듬기" : "아르와 작품 만들기"}
                    </>
                  )}
                </Button>

                {/* AI Chat Toggle */}
                <button
                  onClick={() => setShowChat(!showChat)}
                  className="w-full text-xs text-muted-foreground hover:text-primary flex items-center justify-center gap-1.5 py-1 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  아르에게 작품 설명하기
                </button>
              </CardContent>
            </Card>

            {/* AI Chat Panel */}
            {showChat && (
              <Card className="border-border/60">
                <CardContent className="p-3 space-y-2">
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {chatMessages.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        아르에게 원하는 작품을 자유롭게 설명해보세요.
                      </p>
                    )}
                    {chatMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`text-xs p-2 rounded-lg ${
                          msg.role === "user"
                            ? "bg-primary/10 text-primary ml-6"
                            : "bg-muted text-foreground mr-6"
                        }`}
                      >
                        {msg.content}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleChatSend()}
                      placeholder="예: 좀 더 밝은 느낌으로..."
                      className="flex-1 text-xs border rounded-lg px-3 py-2 bg-background"
                    />
                    <Button size="sm" variant="ghost" onClick={handleChatSend}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl md:text-2xl text-primary">생성된 작품</h2>
              {orderedImages.length > 0 && !isBusy && (
                <button
                  onClick={runGenerate}
                  disabled={isLoading}
                  className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  아르와 함께 다듬기
                </button>
              )}
            </div>

            {/* Error State */}
            {generationError && (
              <div key="error-state" className="mb-4 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">이미지 생성에 실패했습니다.</p>
                  <p className="text-xs text-muted-foreground mt-1">{generationError}</p>
                  <Button variant="outline" size="sm" onClick={runGenerate} disabled={isLoading} className="mt-3">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    다시 시도하기
                  </Button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isBusy && (
              <div key="loading-state" className="border border-border/60 rounded-lg p-12 text-center">
                <div className="relative mx-auto w-16 h-16 mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
                  <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                  <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-primary">{loadingMsg}</p>
                <p className="text-xs text-muted-foreground mt-2">아르가 작품을 만들고 있습니다 (15~30초)</p>
              </div>
            )}

            {/* Empty State */}
            {!isBusy && !showConfirmation && orderedImages.length === 0 && !generationError && (
              <div key="empty-state" className="border border-dashed border-border/80 rounded-lg p-12 md:p-16 text-center">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  사진을 업로드하고 스타일을 선택한 후<br />
                  "아르와 작품 만들기"를 눌러주세요.
                </p>
              </div>
            )}

            {/* Art Memo */}
            {artMemo && !isBusy && orderedImages.length > 0 && (
              <div key="art-memo" className="mb-4 p-4 rounded-lg bg-amber-50/80 border border-amber-200/60">
                <div className="flex items-start gap-2.5">
                  <span className="text-lg leading-none mt-0.5">🎨</span>
                  <div>
                    <p className="text-xs font-medium text-amber-800 mb-1">아르의 메모</p>
                    <p className="text-sm text-amber-900/80 leading-relaxed">{artMemo}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Understanding Summary - 시스템 이해 내용 한국어 표시
                PRECISE_EDIT responses don't compute an understandingSummary (only
                ART_TRANSFORM's full pipeline does), so the card itself must not
                require one to render — otherwise the confirm/cancel buttons below
                would be unreachable for PRECISE_EDIT requests. */}
            {(showConfirmation || isBusy || orderedImages.length > 0) && (
              <div key="understanding-summary" className={`mb-4 p-4 rounded-lg border ${showConfirmation ? "bg-primary/5 border-primary/30" : "bg-muted/40 border-border/60"}`}>
                <p className="text-xs font-medium text-primary mb-2.5">
                  {showConfirmation ? "아르가 이렇게 이해했습니다" : "아르가 이해한 내용"}
                </p>
                {understandingSummary && (
                  <div className="space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">보존</span>
                      <p className="text-sm text-foreground/80">{understandingSummary.preservationSummary}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">배경</span>
                      <p className="text-sm text-foreground/80">{understandingSummary.backgroundSummary}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">스타일</span>
                      <p className="text-sm text-foreground/80">{understandingSummary.styleSummary}</p>
                    </div>
                  </div>
                )}
                {/* PRECISE_EDIT — client-built 변경/유지/방식 summary (see
                    buildPreciseEditSummary above); only shown when the server
                    didn't already send an ART_TRANSFORM understandingSummary. */}
                {!understandingSummary && preciseEditSummary && (
                  <div className="space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">변경</span>
                      <p className="text-sm text-foreground/80">{preciseEditSummary.change}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">유지</span>
                      <p className="text-sm text-foreground/80">{preciseEditSummary.preserve}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary/70 text-xs mt-0.5 font-semibold shrink-0">방식</span>
                      <p className="text-sm text-foreground/80">{preciseEditSummary.method}</p>
                    </div>
                  </div>
                )}
                {showConfirmation && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={confirmAndGenerate}
                      disabled={generateSmart.isPending}
                      className="flex-1 bg-primary text-primary-foreground text-sm font-medium py-2.5 px-4 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {generateSmart.isPending ? "생성 중..." : "이대로 생성하기"}
                    </button>
                    <button
                      onClick={cancelConfirmation}
                      disabled={generateSmart.isPending}
                      className="flex-1 bg-muted text-muted-foreground text-sm font-medium py-2.5 px-4 rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50"
                    >
                      수정하기
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Generated Images Grid */}
            {!isBusy && orderedImages.length > 0 && (
              <Fragment key="generated-grid">
                <div className={`grid gap-4 md:gap-5 ${orderedImages.length > 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                  {orderedImages.map((img, idx) => (
                    <div
                      key={img.id}
                      className={`relative rounded-lg overflow-hidden border-2 bg-card transition-colors ${
                        selectedId === img.id
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-border/60"
                      }`}
                    >
                      {/* Whole artwork, never cropped (object-contain, natural
                          aspect ratio). Tapping/clicking opens the same large
                          preview as the "크게 보기" button — no hover needed. */}
                      <button
                        type="button"
                        onClick={() => { setPreviewImageLoading(true); setPreviewIndex(idx); }}
                        aria-label="작품 크게 보기"
                        className="relative flex w-full items-center justify-center bg-muted/40 p-2"
                      >
                        <img
                          src={img.url}
                          alt={img.prompt}
                          className="block h-auto w-auto max-h-[60vh] max-w-full object-contain"
                          loading="lazy"
                        />
                        {selectedId === img.id && (
                          <div className="absolute top-3 right-3 bg-primary text-primary-foreground text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            선택됨
                          </div>
                        )}
                        {(img as any).qualityScore && (
                          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded">
                            품질 {(img as any).qualityScore}점
                          </div>
                        )}
                      </button>
                      <div className="flex gap-2 p-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { setPreviewImageLoading(true); setPreviewIndex(idx); }}
                          className="h-11 flex-1 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          크게 보기
                        </Button>
                        <Button
                          type="button"
                          variant={selectedId === img.id ? "secondary" : "default"}
                          onClick={() => setSelectedId(img.id)}
                          className="h-11 flex-1 text-sm"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          {selectedId === img.id ? "선택됨" : "이 작품 선택"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    size="lg"
                    disabled={!selectedId}
                    onClick={goNext}
                    className="rounded-full px-8 text-base"
                  >
                    이 작품으로 주문하기 →
                  </Button>
                </div>
              </Fragment>
            )}

            {/* Image Preview Modal */}
            {previewIndex !== null && orderedImages[previewIndex] && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                onClick={() => setPreviewIndex(null)}
                role="dialog"
                aria-modal="true"
              >
                <button
                  type="button"
                  onClick={() => setPreviewIndex(null)}
                  aria-label="닫기"
                  className="absolute top-3 right-3 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors z-10"
                >
                  <X className="w-6 h-6" />
                </button>

                {previewIndex > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewImageLoading(true); setPreviewIndex(previewIndex - 1); }}
                    aria-label="이전 작품"
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors z-10"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}

                {previewIndex < orderedImages.length - 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewImageLoading(true); setPreviewIndex(previewIndex + 1); }}
                    aria-label="다음 작품"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors z-10"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}

                <div
                  className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {previewImageLoading && (
                    <div className="w-[80vw] h-[60vh] max-w-[600px] bg-white/10 rounded-lg animate-pulse flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
                    </div>
                  )}
                  {/* Whole artwork in one screen: object-contain, capped so
                      the image + CTA row always fit within 85vh. */}
                  <img
                    src={orderedImages[previewIndex].url}
                    alt="작품 확대"
                    className={`h-auto w-auto max-w-full max-h-[calc(85vh-5.5rem)] object-contain rounded-lg shadow-2xl ${previewImageLoading ? 'hidden' : ''}`}
                    onLoad={() => setPreviewImageLoading(false)}
                    onError={() => setPreviewImageLoading(false)}
                  />
                  <div className="mt-3 flex w-full items-center justify-center gap-3">
                    <Button
                      type="button"
                      size="lg"
                      onClick={() => {
                        setSelectedId(orderedImages[previewIndex!].id);
                        setPreviewIndex(null);
                      }}
                      className="h-12 flex-1 max-w-xs rounded-full px-6 text-base font-semibold"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      이 작품 선택
                    </Button>
                    <span className="text-white/70 text-sm shrink-0">
                      {previewIndex + 1} / {orderedImages.length}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SitePageShell>
  );
}

export function StepIndicator({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "사진 → 작품" },
    { n: 2, label: "사이즈 · 옵션" },
    { n: 3, label: "결제 · 배송" },
  ];
  return (
    <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-1">
      {steps.map((s, idx) => (
        <div key={s.n} className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium border transition-colors ${
                s.n === current
                  ? "bg-primary text-primary-foreground border-primary"
                  : s.n < current
                    ? "bg-primary/20 text-primary border-primary/30"
                    : "bg-muted text-muted-foreground border-border"
              }`}
            >
              {s.n < current ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
            </div>
            <div
              className={`text-xs md:text-sm whitespace-nowrap ${
                s.n === current ? "text-primary font-medium" : "text-muted-foreground"
              }`}
            >
              {s.label}
            </div>
          </div>
          {idx < steps.length - 1 && <div className="w-6 md:w-12 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}
